import dayjs from 'dayjs'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { boolean, mixed, object, ref, string } from 'yup'

import { parseGravityDateValue } from '@/utils/form/date-format'
import {
  getRequiredOneOfFields,
  hasMeaningfulValue,
  parseRequiredOneOfGroup
} from '@/utils/form/required-groups'

import { isHappa } from '@/config/brand'
import { WPCF7FormField } from '@/types'

const MIN_AGE = 16

export const createYupSchema = (
  config: WPCF7FormField[],
  lang = 'en',
  visibleFieldIds?: Set<string>,
  bypassPasswordValidationEmail?: string
) => {
  const errors = require(`@/utils/form/errors/${lang}.json`)

  // Find password field input key for password_confirmation validation
  const passwordField = config.find((c) => c.adminLabel === 'password')
  const passwordInputKey = passwordField ? `input_${passwordField.id}` : null
  const emailField = config.find((c) => c.adminLabel === 'email')
  const emailInputKey = emailField ? `input_${emailField.id}` : null
  const normalizedBypassEmail = bypassPasswordValidationEmail?.trim().toLowerCase()

  const applyRequiredOneOfValidation = (value: any, item: WPCF7FormField) => {
    const requiredOneOfGroup = parseRequiredOneOfGroup(item)

    if (!requiredOneOfGroup || !item.isRequired) return value

    const groupFields = getRequiredOneOfFields(config, requiredOneOfGroup, visibleFieldIds)
    if (groupFields.length <= 1) return value

    return value.test(
      `required-one-of-${requiredOneOfGroup}`,
      item.errorMessage || errors['required_one_of'] || errors['required'],
      function (this: { parent?: Record<string, unknown> }, currentValue: unknown) {
        if (hasMeaningfulValue(currentValue)) return true

        return groupFields.some((field) => {
          const fieldKey = `input_${field.id}`
          return hasMeaningfulValue(this.parent?.[fieldKey])
        })
      }
    )
  }

  const shouldUseIndividualRequired = (item: WPCF7FormField, shouldBeRequired: boolean) => {
    if (!shouldBeRequired) return false

    const requiredOneOfGroup = parseRequiredOneOfGroup(item)
    if (!requiredOneOfGroup) return true

    const groupFields = getRequiredOneOfFields(config, requiredOneOfGroup, visibleFieldIds)
    return groupFields.length <= 1
  }

  const schema = config
    .filter(
      (c) =>
        c.type !== 'space' &&
        c.type !== 'title' &&
        c.type !== 'multiple_dates' &&
        c.type !== 'section' &&
        c.type !== 'html'
    )
    .reduce((obj, item) => {
      // Check if field is visible (for conditional required validation)
      const isVisible = !visibleFieldIds || visibleFieldIds.has(item.id)
      const shouldBeRequired = item.isRequired && isVisible
      let value: any = mixed()
      if (
        item.type === 'text' ||
        item.type === 'date' ||
        item.type === 'textarea' ||
        item.type === 'select' ||
        item.type === 'radio' ||
        item.type === 'address' ||
        item.type === 'website' ||
        item.type === 'hidden'
      ) {
        value = string()
        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value.required(errors['required'])
        value = applyRequiredOneOfValidation(value, item)
        if (item.type === 'date' && item.adminLabel === 'date_of_birth') {
          value = value.test(
            'min-age',
            errors['min_age']?.replace('${min}', String(MIN_AGE)) || `Minimum age is ${MIN_AGE}`,
            (val: string | undefined) => {
              if (!val) return true

              const parsedDate = parseGravityDateValue(val, item.dateFormat)
              if (!parsedDate) return false

              return dayjs().diff(parsedDate, 'year') >= MIN_AGE
            }
          )
        }
      }
      if (item.type === 'email') {
        value = string().email(errors['email'])

        // Validate allowed domains from errorMessage if specified
        if (item.errorMessage) {
          const allowedDomains = item.errorMessage
            .split(',')
            .map((d: string) => d.trim().toLowerCase())
            .filter((d: string) => d.startsWith('@'))

          if (allowedDomains.length > 0) {
            value = value.test(
              'email-domain',
              `${errors['email_domain']} ${allowedDomains.join(', ')}`,
              (email: string | undefined) => {
                if (!email) return true // Let required handle empty values
                const lowerEmail = email.toLowerCase()
                return allowedDomains.some((domain: string) => lowerEmail.endsWith(domain))
              }
            )
          }
        }

        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value.required(errors['required'])
        value = applyRequiredOneOfValidation(value, item)
      }
      if (item.enablePasswordInput) {
        value = string()
        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value.required(errors['required'])
        value = applyRequiredOneOfValidation(value, item)

        if (item.adminLabel === 'password') {
          value = value.test(
            'password-strength-or-bypass',
            errors['password']?.replace('${min}', '8') || 'Password must be at least 8 characters',
            function (this: { parent?: Record<string, unknown> }, password: string | undefined) {
              if (!password) return true // Let required validation handle empty values

              if (normalizedBypassEmail && emailInputKey) {
                const currentEmail = String(this.parent?.[emailInputKey] || '')
                  .trim()
                  .toLowerCase()

                if (currentEmail === normalizedBypassEmail) return true
              }

              return password.length >= 8
            }
          )
        }

        // Handle password_confirmation validation
        if (item.adminLabel === 'password_confirmation' && passwordInputKey) {
          value = value.oneOf([ref(passwordInputKey)], errors['confirm_password'])
        }
      }
      if (item.type === 'confirm_password') {
        value = string()
        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value
            .required(errors['required'])
            .oneOf([ref('user_pass'), null], errors['confirm_password'])
        else value = value.oneOf([ref('user_pass'), null], errors['confirm_password'])
        value = applyRequiredOneOfValidation(value, item)
      }
      if (item.type === 'tel' || item.type === 'phone') {
        value = string().test('phone-with-prefix', errors['tel'], (phone: string | undefined) => {
          if (!phone) return true // Let required validation handle empty values
          // Validate international format: + followed by digits
          const normalizedPhone = phone.replace(/[^\d+]/g, '')
          if (!normalizedPhone.startsWith('+')) return false

          const parsedPhone = parsePhoneNumberFromString(normalizedPhone)
          return parsedPhone?.isValid() ?? false
        })

        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value.required(errors['required'])
        value = applyRequiredOneOfValidation(value, item)
      }
      if (item.type === 'checkbox' || item.type === 'consent') {
        value = boolean()
        if (shouldBeRequired)
          value = value.oneOf([true], errors['checkbox']).required(errors['required'])
      }
      if (item.type === 'file' || item.type === 'fileupload') {
        value = mixed()
        if (shouldBeRequired) value = value.required(errors['required'])
      }
      if (item.type === 'multi_choice') {
        value = mixed() // Array di stringhe
        if (shouldBeRequired) value = value.required(errors['required'])
      }
      // IBAN validation (bank_account field holds the owner's IBAN), Happa only
      if (isHappa && item.adminLabel === 'bank_account') {
        const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/
        value = string().matches(ibanRegex, errors['iban'])
        if (shouldUseIndividualRequired(item, shouldBeRequired))
          value = value.required(errors['required'])
        value = applyRequiredOneOfValidation(value, item)
      }

      return {
        ...obj,
        [`input_${item.id}`]: value
      }
    }, {})

  return object().shape(schema)
}

export const createInitialValues = (fields: WPCF7FormField[], initialValues = []) => {
  const values = fields.reduce((obj, item) => {
    return {
      ...obj,
      [`input_${item.id}`]: item.type === 'consent' ? false : item.type === 'multi_choice' ? [] : ''
    }
  }, {})
  return Object.assign(values, ...initialValues)
}

export const convertJsontoFormData = (obj: any) => {
  const formData = new FormData()

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key]
      if (Array.isArray(value)) {
        // Per gli array (come multi_choice), appendi ogni elemento
        value.forEach((item, index) => {
          formData.append(`${key}[${index}]`, item)
        })
      } else {
        formData.append(key, value)
      }
    }
  }
  return formData
}
